import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL from '../../api';
import Swal from 'sweetalert2';
import Header from '../../layouts/Header';
import SideBar from '../../layouts/SideBar';
import { FaCheckCircle, FaTimesCircle, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const WarehouseReport = () => {
  const [warehouseData, setWarehouseData] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('token');

  const fetchCustomers = async () => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication token missing');
      const res = await fetch(`${BASE_URL}/api/Customer/CustomerList`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCustomerList(Array.isArray(data.Data) ? data.Data : []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchWarehouseData = async () => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication token missing');

      const requestBody = {
        customerid: selectedCustomer ? parseInt(selectedCustomer) : 0,
        docketnumber: debouncedSearchTerm || '',
        fromddate: fromDate ? new Date(fromDate).toISOString() : null,
        todate: toDate ? new Date(toDate).toISOString() : null,
        operationflag: 'WorkhouseReport'
      };

      const res = await fetch(`${BASE_URL}/api/Reports/WorkhouseReportList`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await res.json();
      setWarehouseData(Array.isArray(data.Data) ? data.Data : []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);
  useEffect(() => { fetchWarehouseData(); }, [debouncedSearchTerm, fromDate, toDate, selectedCustomer]);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const indexOfLast = currentPage * recordsPerPage;
  const currentRecords = warehouseData.slice(indexOfLast - recordsPerPage, indexOfLast);
  const pageCount = Math.ceil(warehouseData.length / recordsPerPage);

  const renderPagination = () => (
    <div className="flex space-x-2 justify-center mt-6">
      <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)} className="bg-blue-500 text-white px-3 py-1 rounded disabled:bg-gray-300">Prev</button>
      {Array.from({ length: pageCount }, (_, i) => (
        <button key={i + 1} onClick={() => setCurrentPage(i + 1)} className={`px-3 py-1 rounded ${currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          {i + 1}
        </button>
      ))}
      <button disabled={currentPage === pageCount} onClick={() => setCurrentPage(prev => prev + 1)} className="bg-blue-500 text-white px-3 py-1 rounded disabled:bg-gray-300">Next</button>
    </div>
  );

  const exportToExcel = () => {
    const dataToExport = warehouseData.map((item, index) => ({
      "S.No": index + 1,
      "Doc No": item.docno || 'N/A',
      "Consignor": item.consignor || 'N/A',
      "Consignee": item.consignee || 'N/A',
      "Origin": item.origin || 'N/A',
      "Destination": item.destination || 'N/A',
      "Booking Date": item.bookingdate ? new Date(item.bookingdate).toLocaleDateString() : 'N/A',
      "Weight": item.weight?.toFixed(2) || '0.00',
      "Delivery Status": item.deliverystatus || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "WarehouseReport");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(dataBlob, "WarehouseReport.xlsx");
  };

  return (
    <>
      <div className="px-6">
      <ToastContainer />
        <h3 className="text-3xl font-semibold mb-6 text-gray-700">Warehouse Report</h3>

        {warehouseData.length > 0 && (
          <div className="flex justify-end mb-4">
            <button onClick={exportToExcel} className="flex items-center bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
              <FaFileExcel className="mr-2" />
              Export to Excel
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <input
            className="border p-2 rounded"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            className="border p-2 rounded"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <select
            className="border p-2 rounded"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">All Customers</option>
            {customerList.map(c => (
              <option key={c.customerid} value={c.customerid}>{c.name}</option>
            ))}
          </select>
          <input
            className="border p-2 rounded"
            type="text"
            placeholder="Search Doc No"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {isLoading ? (
          <div className="text-center">Loading...</div>
        ) : (
          <>
            <table className="w-full border shadow rounded">
              <thead className="bg-gray-100 text-sm">
                <tr>
                  <th className="border px-2 py-1">S.No</th>
                  <th className="border px-2 py-1">Doc No</th>
                  <th className="border px-2 py-1">Consignor</th>
                  <th className="border px-2 py-1">Consignee</th>
                  <th className="border px-2 py-1">Origin</th>
                  <th className="border px-2 py-1">Destination</th>
                  <th className="border px-2 py-1">Booking Date</th>
                  <th className="border px-2 py-1">Weight</th>
                  <th className="border px-2 py-1">Delivery</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.length > 0 ? currentRecords.map((r, i) => (
                  <tr key={i} className="text-sm">
                    <td className="border px-2 py-1">{(currentPage - 1) * recordsPerPage + i + 1}</td>
                    <td className="border px-2 py-1">{r.docno || 'N/A'}</td>
                    <td className="border px-2 py-1">{r.consignor || 'N/A'}</td>
                    <td className="border px-2 py-1">{r.consignee || 'N/A'}</td>
                    <td className="border px-2 py-1">{r.origin || 'N/A'}</td>
                    <td className="border px-2 py-1">{r.destination || 'N/A'}</td>
                    <td className="border px-2 py-1">{r.bookingdate ? new Date(r.bookingdate).toLocaleDateString() : 'N/A'}</td>
                    <td className="border px-2 py-1">{r.weight?.toFixed(2) || '0.00'}</td>
                    <td className="border px-2 py-1">
                      <div className="flex items-center space-x-1">
                        {r.deliverystatus === 'Delivered'
                          ? <FaCheckCircle className="text-green-500" />
                          : <FaTimesCircle className="text-red-500" />}
                        <span>{r.deliverystatus || 'N/A'}</span>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4">No records found</td>
                  </tr>
                )}
              </tbody>
            </table>
            {pageCount > 1 && renderPagination()}
          </>
        )}
      </div>
    </>
  );
};

export default WarehouseReport;
